import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { errMsg } from "./OutreachSendButton";
import { ActionButtons, ED } from "./ActionButtons";
import { MailCard } from "./PartnerMailQueue";
import { AnswerEditor } from "./PartnerQuestionsQueue";
import { VisualReplace } from "./VisualReplace";

// ACTION CENTER « À traiter » (v2.1.6.4.1.5) — une seule vue : Gilles comprend en moins de cinq secondes si le système a besoin de lui, pourquoi, et ce qu'il doit faire.
// Agrège (sans dupliquer la logique métier) : questions Partner Intelligence, mails partenaires, validations éditoriales, visuels, cycles interrompus, brouillons.
// Contrat API : GET /editorial/action-center?type=questions|mails|human_review|visuals|errors|drafts → { summary, items[] } ; chaque item porte ses actions réelles (méthode, route, confirmation, état).
const FILTERS = [["", "Tout"], ["questions", "Questions"], ["mails", "Mails à valider"], ["human_review", "Validations éditoriales"], ["visuals", "Visuels"], ["errors", "Erreurs"], ["drafts", "Brouillons"]];
const QUESTION_LABELS = { answer_yes: "Oui", answer_no: "Non", answer_unknown: "Je ne sais pas", ignore: "Ignorer / non pertinent", context: "Voir le contexte" };   // libellés de repli (le serveur fournit déjà les siens)
const PRIORITY_STYLE = { CRITICAL: "bg-red-600 text-white", HIGH: "bg-red-100 text-red-900", NORMAL: "bg-slate-100 text-slate-800", LOW: "bg-slate-50 text-slate-500" };

const HEADLINE = { yes: (n) => `Votre attention est requise — ${n} élément${n > 1 ? "s" : ""}`, no: "✓ Rien ne requiert votre attention.", sub: "ITERCATUS Intelligence poursuit son travail." };   // repli si le résumé serveur n'a pas de titre

export const AttentionBanner = ({ summary, onOpen }) => {
  if (!summary) return null;
  summary = { ...summary, headline: summary.headline || (summary.needs_attention ? HEADLINE.yes(summary.badge) : HEADLINE.no), subline: summary.subline || HEADLINE.sub };
  return summary.needs_attention ? (
    <button type="button" onClick={onOpen} className="w-full text-left rounded-xl border-2 border-red-300 bg-red-50 p-4 flex items-center gap-3" data-testid="intel-attention-banner">
      <AlertTriangle className="w-6 h-6 text-red-700 shrink-0" />
      <span className="text-lg font-semibold text-red-900">{summary.headline}</span>
      <Badge className="bg-red-600 text-white ml-auto" data-testid="intel-attention-badge">{summary.badge}</Badge>
    </button>
  ) : (
    <div className="rounded-xl border border-green-200 bg-green-50 p-4 flex items-center gap-3" data-testid="intel-attention-clear">
      <CheckCircle2 className="w-6 h-6 text-green-700 shrink-0" />
      <div><p className="font-semibold text-green-900">{summary.headline}</p><p className="text-sm text-green-800">{summary.subline}</p></div>
    </div>
  );
};

const Know = ({ lines }) => (lines || []).length ? (
  <div className="text-sm"><p className="font-semibold">Ce que je sais :</p><ul className="list-disc pl-5">{lines.map((l, i) => <li key={i}>{l}</li>)}</ul></div>
) : null;

const QuestionBody = ({ it, done, setDone, onAnswered }) => {
  const [val, setVal] = useState("");
  const [ctx, setCtx] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const d = it.details;
  const ui = it.actions.find((a) => a.ui === "choice" || a.ui === "text");
  const send = async (answer) => {
    try {
      const r = await axios.post(`${ED}/partner-intel/questions/${it.target.id}/answer`, { answer, resume: true });
      setDone(r.data);
      toast.success("Réponse enregistrée");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onUi = async (a) => {
    if (a.ui === "context") { try { setCtx((await axios.get(`${ED}${a.path}`)).data); } catch (e) { toast.error(errMsg(e)); } }
    if (a.ui === "choice" || a.ui === "text") send(val);
  };
  if (done) {
    const ready = done.run?.final_status === "READY_FOR_GILLES" && done.run?.outreach_id;
    return (
      <div className="space-y-2" data-testid="intel-question-done">
        <p className="text-green-800 font-semibold">✓ Réponse enregistrée.</p>
        <p className="text-sm">Partner Intelligence reprend son travail.</p>
        {ready && <p className="text-green-800 font-semibold">✓ Message prêt à être relu — {!mailOpen && <Button size="sm" onClick={() => setMailOpen(true)} data-testid="intel-question-open-mail">Relire le mail</Button>}</p>}
        {ready && mailOpen && <MailCard summary={{ id: done.run.outreach_id, status: "PENDING_APPROVAL" }} startOpen onChange={() => onAnswered?.(done)} />}
        <Button size="sm" variant="ghost" onClick={() => onAnswered?.(done, "close")} data-testid="intel-question-close">Fermer</Button>
        <Button size="sm" variant="outline" onClick={() => setEdit(!edit)} data-testid="intel-question-modify">Modifier ma réponse</Button>
        {edit && done.question && <AnswerEditor q={done.question} onDone={() => { setEdit(false); onAnswered?.(done, "refresh"); }} />}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-base font-semibold" data-testid="intel-question-text">{d.question}</p>
      <Know lines={d.what_i_know} />
      <p className="text-xs text-muted-foreground">{d.why}</p>
      {d.answer_type === "choice" && <div className="flex flex-wrap gap-3">{(d.choices || []).map((c) => <label key={c.value} className="text-sm flex items-center gap-1"><input type="radio" name={it.id} checked={val === c.value} onChange={() => setVal(c.value)} />{c.label}</label>)}</div>}
      {d.answer_type === "text" && <Input className="max-w-xl" placeholder="Votre réponse" value={val} onChange={(e) => setVal(e.target.value)} data-testid="intel-question-answer" />}
      <ActionButtons actions={it.actions.map((a) => (a.ui === "choice" || a.ui === "text" ? { ...a, enabled: !!val, disabled_reason: "choisissez ou saisissez une réponse" } : { ...a, label: a.label || QUESTION_LABELS[a.id] }))} onUi={onUi}
        onDone={(data) => setDone(data.question ? data : { question: data, run: null })} idPrefix="intel-question-action" />
      {ctx && <div className="rounded border p-2 text-sm bg-slate-50" data-testid="intel-question-context"><p className="font-semibold">Contexte</p><ul className="list-disc pl-5">{ctx.what_i_know.map((l, i) => <li key={i}>{l}</li>)}</ul>
        {(ctx.facts || []).length > 0 && <p className="text-xs text-muted-foreground mt-1">{ctx.facts.length} élément(s) enregistré(s) pour cette organisation.</p>}</div>}
      {!ui && null}
    </div>
  );
};

const ReviewBody = ({ it, onDone }) => {
  const [open, setOpen] = useState(null);
  const onUi = async (a) => { if (a.ui === "open_review") { try { setOpen((await axios.get(`${ED}${a.path}`)).data); } catch (e) { toast.error(errMsg(e)); } } };
  return (
    <div className="space-y-2">
      <p className="text-base font-semibold">{it.explanation}</p>
      {open && <div className="rounded border p-2 text-sm bg-slate-50" data-testid="intel-review-detail">{(open.material_issues || []).map((m, i) => <p key={i}><b>{m.summary}</b> — {m.why_material}</p>)}{(open.minor_edits || []).length > 0 && <p className="text-xs">Retouches mineures : {open.minor_edits.map((m) => m.summary).join(" ; ")}</p>}</div>}
      <ActionButtons actions={it.actions} onUi={onUi} onDone={onDone} idPrefix="intel-review-action" />
    </div>
  );
};

const Card = ({ it, onChange, done, setDone, onClose }) => {
  const [more, setMore] = useState(false);
  const [mail, setMail] = useState(false);
  const [replace, setReplace] = useState(false);
  const typeLabel = { partner_question: "Question", partner_mail: "Mail partenaire", human_review: "Validation éditoriale", visual_review: "Visuel", run_error: "Cycle interrompu", draft_ready: "Brouillon" }[it.type];
  return (
    <article className="rounded-xl border border-border bg-white p-4 space-y-3" data-testid={`intel-ac-item-${it.id}`}>
      <header className="flex flex-wrap items-center gap-2">
        <Badge className={PRIORITY_STYLE[it.priority]} data-testid="intel-ac-priority">{it.priority}</Badge>
        <span className="text-xs uppercase tracking-wide text-muted-foreground">{typeLabel}</span>
        <h3 className="font-semibold">{it.title}</h3>
        {it.contact && <span className="text-sm text-muted-foreground">· {it.contact}</span>}
      </header>
      {it.type === "partner_question" && <QuestionBody it={it} done={done} setDone={(d) => { setDone(d); onChange?.(true); }} onAnswered={(d, what) => { if (what === "close") onClose?.(); else onChange?.(true); }} />}
      {it.type === "partner_mail" && (
        <div className="space-y-2">
          <p className="text-base font-semibold">{it.explanation}</p>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <p><b>Destinataire :</b> {it.details.recipient}</p><p><b>Relation détectée :</b> {it.details.relationship}</p>
            <p><b>Objectif :</b> {it.details.objective || "—"}</p><p><b>Partner Reviewer :</b> {it.details.reviewer_opinion}</p>
          </div>
          <ActionButtons actions={it.actions} onUi={(a) => { if (a.ui === "open_mail" || a.ui === "edit") setMail(true); }} onDone={onChange} exclude={mail ? ["open"] : []} idPrefix="intel-mail-action" />
          {mail && <MailCard summary={{ id: it.target.id, status: "PENDING_APPROVAL" }} startOpen onChange={onChange} />}
        </div>
      )}
      {it.type === "human_review" && <ReviewBody it={it} onDone={onChange} />}
      {it.type === "visual_review" && (
        <div className="space-y-2">
          <p className="text-base font-semibold">{it.explanation}</p>
          <div className="flex flex-wrap gap-3 items-start">
            {it.details.image_url && <img src={it.details.image_url} alt="Visuel officiel à valider" className="max-h-40 max-w-full rounded border object-contain" loading="lazy" />}
            <div className="text-sm"><p><b>Source :</b> {it.details.source}</p><p><b>Crédit :</b> {it.details.credit || "non précisé"}</p>{it.details.rights_excerpt && <p className="text-xs text-muted-foreground">« {it.details.rights_excerpt} »</p>}</div>
          </div>
          <ActionButtons actions={it.actions} onDone={onChange} onUi={(a) => { if (a.ui === "replace") setReplace(true); }} idPrefix="intel-visual-action" />
          {replace && <VisualReplace subjectId={it.target.id} onDone={onChange} onClose={() => setReplace(false)} />}
        </div>
      )}
      {["run_error", "draft_ready"].includes(it.type) && (
        <div className="space-y-2"><p className="text-base font-semibold">{it.explanation}</p><ActionButtons actions={it.actions} onDone={onChange} idPrefix="intel-ac-action" /></div>
      )}
      <button type="button" className="text-xs underline text-muted-foreground" onClick={() => setMore(!more)} data-testid="intel-ac-details-toggle">{more ? "Masquer les détails" : "Voir les détails"}</button>
      {more && <pre className="text-xs bg-slate-50 rounded p-2 overflow-x-auto" data-testid="intel-ac-details">{JSON.stringify(it.details.technical || {}, null, 2)}</pre>}
    </article>
  );
};

export const ActionCenter = ({ initialFilter = "", refreshKey, onChange }) => {
  const [filter, setFilter] = useState(initialFilter || "");
  const [data, setData] = useState(null);
  const [kept, setKept] = useState({});          // cartes traitées gardées à l'écran (« ✓ Réponse enregistrée », « Message prêt à être relu ») jusqu'à fermeture : le parcours reste continu
  useEffect(() => { setFilter(initialFilter || ""); }, [initialFilter]);
  const load = useCallback(async () => { try { setData((await axios.get(`${ED}/action-center`, { params: filter ? { type: filter } : {} })).data); } catch (e) { toast.error(errMsg(e)); } }, [filter]);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!data) return null;
  const ids = new Set(data.items.map((i) => i.id));
  const keptOnly = Object.values(kept).filter((k) => !ids.has(k.item.id)).map((k) => k.item);
  const items = [...keptOnly, ...data.items];
  return (
    <div className="space-y-4" data-testid="intel-action-center">
      <div className="flex flex-wrap gap-2">{FILTERS.map(([v, l]) => <Button key={v || "all"} size="sm" variant={filter === v ? "default" : "outline"} onClick={() => setFilter(v)} data-testid={`intel-ac-filter-${v || "all"}`}>{l}</Button>)}</div>
      {items.length === 0 && <p className="text-sm text-muted-foreground" data-testid="intel-ac-empty">Rien à traiter dans cette vue.</p>}
      <div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
        {items.map((it) => <Card key={it.id} it={it} done={kept[it.id]?.done} setDone={(d) => setKept((k) => ({ ...k, [it.id]: { item: it, done: d } }))}
          onClose={() => setKept((k) => { const n = { ...k }; delete n[it.id]; return n; })} onChange={(silent) => { load(); onChange?.(); }} />)}
      </div>
    </div>
  );
};
